import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { getTicketForm, type TicketForm } from '@/lib/ticket-form-api';
import { getTicketFields, type TicketFieldRegistryField } from '@/lib/ticket-field-registry-api';
import {
  DynamicTicketFieldRenderer,
  mergeTicketFormFields,
} from '@/components/forms/DynamicTicketFieldRenderer';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, PageHeader, SkeletonText } from '@/components/ui';


export function TicketFormPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const errorMessage = React.useCallback(
    (error: unknown) => (error instanceof ApiError ? error.message : t('set.tp.loadError')),
    [t],
  );
  const [form, setForm] = React.useState<TicketForm | null>(null);
  const [registry, setRegistry] = React.useState<TicketFieldRegistryField[]>([]);
  const [values, setValues] = React.useState<Record<string, unknown>>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [nextForm, nextRegistry] = await Promise.all([getTicketForm(id), getTicketFields()]);
      setForm(nextForm);
      setRegistry(nextRegistry);
      setValues({});
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [id, errorMessage]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (!id) {
    return <ErrorState message={t('set.formIdRequired')} />;
  }
  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Card><CardContent className="p-6"><SkeletonText lines={2} /></CardContent></Card>
        <Card><CardContent className="p-6"><SkeletonText lines={6} /></CardContent></Card>
      </div>
    );
  }
  if (error || !form) {
    return <ErrorState message={error ?? t('set.formNotFound')} onRetry={() => void load()} />;
  }

  const fields = mergeTicketFormFields(form, registry);
  const visibleFields = fields.filter((field) => field.enabled);
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[
          { label: t('set.settings'), to: '/settings' },
          { label: t('set.ticketForms'), to: `/settings/ticket-forms/${id}` },
          { label: t('set.tp.preview') },
        ]}
        title={form.name}
        description={t('set.tp.customerPreview')}
        actions={(
          <>
            <Badge variant={form.isPublished ? 'success' : 'outline'}>{form.isPublished ? t('set.published') : t('set.draft')}</Badge>
            <Link to={`/settings/ticket-forms/${id}`}><Button variant="outline">{t('set.tp.backToBuilder')}</Button></Link>
          </>
        )}
      />

      <Card className="border-brand-200 bg-brand-50">
        <CardContent className="p-4 text-sm text-brand-900">
          <p className="font-medium">{t('set.tp.modeTitle')}</p>
          <p className="mt-1 text-brand-800">{t('set.tp.modeDesc')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{form.name}</CardTitle>
          {form.description && <p className="text-sm text-ink-subtle">{form.description}</p>}
        </CardHeader>
        <CardContent>
          {visibleFields.length === 0 ? (
            <EmptyState
              title={t('set.tp.emptyTitle')}
              description={t('set.tp.emptyDesc')}
            />
          ) : (
            <DynamicTicketFieldRenderer
              fields={fields}
              values={values}
              rules={form.rules}
              mode="preview"
              onChange={(fieldKey, value) => setValues((current) => ({ ...current, [fieldKey]: value }))}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
